import React from "react";
import { Log, LogHeader, LogBody } from "~/components/shared/Log";
import { useTheme } from "~/components/job/Job/AgentLog/useTheme";
import { useAgentLogs } from "~/components/job/Job/AgentLog/useAgentLogs";
import { useInfiniteScroll } from "~/components/job/Job/AgentLog/useInfiniteScroll";
import ThemeToggleControl from "~/components/job/Job/AgentLog/ThemeToggleControl";
import {
  LoadingMessage,
  LoadingMoreMessage,
  NoLogsMessage,
  NotHostedMessage,
  ErrorMessage,
} from "~/components/job/Job/AgentLog/LogMessages";
import classNames from "classnames";

type Props = {
  job: {
    id: string;
    basePath: string;
    state: string;
    hosted?: boolean;
    agentName?: string;
  };
};

export default function AgentLog({ job }: Props) {
  const { theme, toggleTheme } = useTheme();
  const { loading, loadingMore, error, content, hasMoreData, loadMore } = useAgentLogs(job);
  const sentinelRef = useInfiniteScroll(loadMore, hasMoreData, loadingMore);

  const renderLogContent = () => {
    if (!job.hosted) {
      return <NotHostedMessage />;
    }

    if (error) {
      return <ErrorMessage error={error} />;
    }

    if (!content && loading) {
      return <LoadingMessage />;
    }

    if (!content) {
      return <NoLogsMessage />;
    }

    return (
      <>
        <div
          className="log-content whitespace-pre-wrap p-4 font-mono text-xs"
          dangerouslySetInnerHTML={{ __html: content }}
        />
        {hasMoreData && <div ref={sentinelRef} className="h-1" />}
        {loadingMore && <LoadingMoreMessage />}
      </>
    );
  };

  return (
    <div
      data-testid="AgentLogComponent"
      className={classNames("AgentLogComponent not-inverted", {
        "mt-[10px]": !Features.BuildSidebar,
        "!flex min-h-0 flex-auto flex-col": Features.BuildSidebar,
      })}
      data-theme={theme}
    >
      <Log className="min-h-0 p-0">
        <LogHeader
          className={classNames("flex gap-2", {
            "border-b border-gray-200 p-2": !Features.BuildSidebar,
          })}
        >
          <ThemeToggleControl theme={theme} onToggle={toggleTheme} />
        </LogHeader>
        <LogBody>{renderLogContent()}</LogBody>
      </Log>
    </div>
  );
}
